import { useQuery } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { subDays, startOfDay } from 'date-fns';

import useApi from '../../use-api';

import type {
  INewPatientsReportListFilters,
  IPartialPaginationMeta,
  IRpmNewPatients,
} from 'infrastructure/interfaces';

export type TNewPatientsReportFilter = INewPatientsReportListFilters &
  IPartialPaginationMeta;

const apiUrl = `reports/reports/new-patients`;

const prepareFilters = (filters?: TNewPatientsReportFilter) => {
  if (!filters) return {};
  const copiedFilters = structuredClone(filters);

  if (copiedFilters.range) {
    copiedFilters.startTime = subDays(
      startOfDay(new Date()),
      copiedFilters.range,
    ).getTime();
    delete copiedFilters.range;
  }

  return copiedFilters;
};

type UseGetNewPatientsReport = {
  loading: boolean;
  reports: IRpmNewPatients['items'];
  meta: TNewPatientsReportFilter;
};

interface IUseGetNewPatientsReportProps {
  meta?: INewPatientsReportListFilters & IPartialPaginationMeta;
}

export const NEW_PATIENTS_REPORT_PER_PAGE = 100;

export const useGetNewPatientsReport = (
  props: IUseGetNewPatientsReportProps,
): UseGetNewPatientsReport => {
  const { items = NEW_PATIENTS_REPORT_PER_PAGE, page = 1 } = props?.meta ?? {};
  const { loadData } = useApi();

  const preparedFilter = prepareFilters(props?.meta);
  const queryKey = [apiUrl, preparedFilter];

  const { data, isLoading } = useQuery({
    queryKey,
    staleTime: STALE_TIME,
    queryFn: () =>
      loadData<IRpmNewPatients>(apiUrl, {
        sortField: JSON.stringify(props?.meta?.sortField),
        items,
        page,
        ...preparedFilter,
      }).then((res) => {
        if (res) {
          return {
            count: res.count,
            items: res.items,
          };
        }
      }),
    enabled: !!preparedFilter.startTime,
  });

  const meta = {
    totalCount: data?.count ?? 0,
    page,
    items,
    ...props?.meta,
  };

  return {
    loading: isLoading,
    reports: data ? data.items : [],
    meta,
  };
};
